import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getAllLocations, createLocation, updateLocation, deleteLocation } from "@/lib/api";
import { toast } from "sonner";

const initialFormData = {
  name: "",
  city: "",
  address: "",
  is_home_location: false,
  is_active: true,
};

export default function AdminLocations() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await getAllLocations();
      setLocations(response.data);
    } catch (error) {
      console.error("Error fetching locations:", error);
      toast.error("Kon locaties niet laden");
    } finally {
      setLoading(false);
    }
  };

  const openNewDialog = () => {
    setFormData(initialFormData);
    setEditingId(null);
    setDialogOpen(true);
  };

  const handleEdit = (location) => {
    setFormData({
      name: location.name,
      city: location.city || "",
      address: location.address || "",
      is_home_location: location.is_home_location,
      is_active: location.is_active,
    });
    setEditingId(location.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Weet je zeker dat je deze locatie wilt verwijderen?")) return;
    try {
      await deleteLocation(id);
      toast.success("Locatie succesvol verwijderd");
      fetchLocations();
    } catch (error) {
      console.error("Error deleting location:", error);
      toast.error("Kon locatie niet verwijderen");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await updateLocation(editingId, formData);
        toast.success("Locatie succesvol bijgewerkt");
      } else {
        await createLocation(formData);
        toast.success("Locatie succesvol toegevoegd");
      }
      setDialogOpen(false);
      fetchLocations();
    } catch (error) {
      console.error("Error saving location:", error);
      toast.error("Kon locatie niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSwitchChange = (name, checked) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MapPin className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Locaties</h1>
            <p className="text-slate-600">Beheer wedstrijdlocaties</p>
          </div>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="h-4 w-4 mr-2" />
          Locatie Toevoegen
        </Button>
      </div>

      <Card className="bg-white border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-600">Laden...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-200">
                  <TableHead className="text-slate-600">Naam</TableHead>
                  <TableHead className="text-slate-600">Stad</TableHead>
                  <TableHead className="text-slate-600">Adres</TableHead>
                  <TableHead className="text-slate-600">Thuislocatie</TableHead>
                  <TableHead className="text-slate-600">Status</TableHead>
                  <TableHead className="text-right text-slate-600">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locations.length > 0 ? (
                  locations.map((location) => (
                    <TableRow key={location.id} className="border-slate-200">
                      <TableCell className="font-medium text-slate-900">{location.name}</TableCell>
                      <TableCell className="text-slate-600">{location.city || "-"}</TableCell>
                      <TableCell className="text-slate-600">{location.address || "-"}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${location.is_home_location ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"}`}>
                          {location.is_home_location ? "Ja" : "Nee"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs ${location.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"}`}>
                          {location.is_active ? "Actief" : "Inactief"}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleEdit(location)} className="border-slate-200">
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleDelete(location.id)} className="border-slate-200 text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                      Geen locaties gevonden. Voeg je eerste locatie toe!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md bg-white border-slate-200">
          <DialogHeader>
            <DialogTitle className="text-slate-900">{editingId ? "Locatie Bewerken" : "Locatie Toevoegen"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-slate-700">Naam *</Label>
              <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="city" className="text-slate-700">Stad</Label>
              <Input id="city" name="city" value={formData.city} onChange={handleChange} className="border-slate-300" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address" className="text-slate-700">Adres</Label>
              <Textarea id="address" name="address" value={formData.address} onChange={handleChange} className="border-slate-300" rows={2} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-700">Thuislocatie</Label>
              <Switch checked={formData.is_home_location} onCheckedChange={(c) => handleSwitchChange("is_home_location", c)} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-slate-700">Actief</Label>
              <Switch checked={formData.is_active} onCheckedChange={(c) => handleSwitchChange("is_active", c)} />
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-300">Annuleren</Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white">{saving ? "Opslaan..." : "Opslaan"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
